import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  recommendPropose,
  recommendAccept,
  submitContentActionBrief,
  isRecommendDomainChoice,
  recommendHasProposal,
  isRecommendUnavailable,
  isRecommendBlogDraft,
  type AssistNarrationSegment,
  type BlogDraft,
  type BriefChannel,
  type BriefQuestion,
  type ConnectorGate,
  type ContentAction,
  type DomainOption,
  type ProposedAction,
} from '@/data/copilot';
import { ApiError } from '@/lib/api';
import { ConnectorGatedNotice } from './ConnectorGatedNotice.tsx';
import { BlogDraftView } from './BlogDraftView.tsx';
import {
  briefReviewRows,
  briefSteps,
  briefToPlainText,
  channelLabel,
  classifyProposePhase,
  currentStep,
  evidenceLines,
  isFullyGated,
  isLastStep,
  mergeBriefAnswers,
  stepProgressLabel,
  unavailableBriefText,
  type BriefAnswers,
  type ContentActionPhase,
  type DraftFollowUp,
} from './contentActionModel.ts';

/* ----------------------------------------------------------------------------
 * ContentActionPanel (P3 Lane 3D) — the copilot's action conversation, mounted
 * inside CopilotRail. It turns Aura's evidence-grounded content proposal into a
 * guided BRIEF (asking ONLY the missing §2.7 fields, one clear step at a time),
 * submits the completed brief to the existing draft intake, then hands off to
 * the draft for human review → revision → approval.
 *
 * Connectors stay GATED end-to-end: the panel renders NO publish / schedule /
 * send control (see contentActionModel.controlsForPhase), always shows the
 * "draft only" notice, and — before it will prepare an action — asserts the
 * server's connector gate is fully closed. It NEVER fabricates a draft: when the
 * intake is not yet wired the flow ends in an honest "draft workspace connecting"
 * state. All model/answer text is server-cleaned (no raw markdown).
 * -------------------------------------------------------------------------- */

const ASK = 'What should I do next to grow this domain?';

/** Clean, markdown-free copy split into short paragraphs (server pre-cleans it). */
function CleanText({ text }: { text: string }) {
  const paras = (text ?? '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-2 text-sm leading-relaxed text-text-primary">
      {paras.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          {p}
        </p>
      ))}
    </div>
  );
}

function AdvisoryHeader({ label }: { label: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
        {label}
      </span>
      <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
        Advisory
      </span>
    </div>
  );
}

function Thinking({ label }: { label: string }) {
  return (
    <div
      className="space-y-2 rounded-md border border-ai/25 bg-ai-wash/30 p-3"
      role="status"
      aria-label={label}
    >
      <div className="ai-shimmer h-2 w-5/6 rounded-full" />
      <div className="ai-shimmer h-2 w-2/3 rounded-full" />
    </div>
  );
}

/** Shared button style with a mobile-safe (>=44px) tap target. */
const primaryBtn =
  'inline-flex items-center justify-center rounded-full border border-ai bg-ai-wash/60 px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors duration-fast hover:bg-ai-wash max-lg:min-h-[44px]';
const ghostBtn =
  'inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-[11px] text-text-tertiary transition-colors duration-fast hover:bg-hover hover:text-text-primary max-lg:min-h-[44px]';
const outlineBtn =
  'inline-flex items-center justify-center rounded-sm border border-border-default px-2 py-1.5 text-[11px] text-text-secondary transition-colors duration-fast hover:bg-hover hover:text-text-primary max-lg:min-h-[44px]';

export function ContentActionPanel({
  engineKey,
  routePath,
  activeProjectId,
  onDomainSelected,
}: {
  engineKey: string | null;
  routePath: string;
  activeProjectId?: number | null;
  onDomainSelected?: (projectId: number) => void | Promise<void>;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<ContentActionPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  // propose state
  const [reply, setReply] = useState('');
  const [segments, setSegments] = useState<AssistNarrationSegment[]>([]);
  const [proposal, setProposal] = useState<ProposedAction | null>(null);
  const [domainChoices, setDomainChoices] = useState<DomainOption[]>([]);

  // accept / brief state
  const [action, setAction] = useState<ContentAction | null>(null);
  const [questions, setQuestions] = useState<BriefQuestion[]>([]);
  const [intakeModule, setIntakeModule] = useState<'content-studio' | 'marketing'>('content-studio');
  const [connectors, setConnectors] = useState<ConnectorGate | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<BriefAnswers>({});
  const [input, setInput] = useState('');

  // handoff state
  const [draftUrl, setDraftUrl] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState<DraftFollowUp | null>(null);
  const [copied, setCopied] = useState(false);
  // The server's honest roadmap message for the blog_unavailable card.
  const [unavailableNote, setUnavailableNote] = useState('');
  // The populated long-form blog_article draft (P1 happy path, HTTP 200).
  const [blogDraft, setBlogDraft] = useState<BlogDraft | null>(null);

  const reset = () => {
    setPhase('idle');
    setError(null);
    setReply('');
    setSegments([]);
    setProposal(null);
    setDomainChoices([]);
    setAction(null);
    setQuestions([]);
    setConnectors(null);
    setStepIndex(0);
    setAnswers({});
    setInput('');
    setDraftUrl(null);
    setFollowUp(null);
    setCopied(false);
    setUnavailableNote('');
    setBlogDraft(null);
  };

  const failed = () => {
    setPhase('error');
    setError('Aura could not do that just now. Nothing was changed.');
  };

  const applyPropose = (res: Awaited<ReturnType<typeof recommendPropose>>) => {
    setReply(res.reply);
    setSegments(res.narration_segments ?? []);
    const kind = classifyProposePhase(res);
    if (kind === 'domain_choice' && isRecommendDomainChoice(res)) {
      setDomainChoices(res.domains);
      setPhase('domain_choice');
    } else if (kind === 'proposal' && recommendHasProposal(res)) {
      setProposal(res.proposed_action);
      setReply(res.display_text ?? res.reply);
      setPhase('proposal');
    } else {
      setReply(res.display_text ?? res.reply);
      setPhase('no_signal');
    }
  };

  const startSuggest = async (projectId?: number) => {
    setError(null);
    setPhase('proposing');
    try {
      const res = await recommendPropose(ASK, {
        engine: engineKey,
        context: { route: routePath },
        projectId: projectId ?? activeProjectId ?? undefined,
      });
      applyPropose(res);
    } catch {
      failed();
    }
  };

  const pickDomain = async (d: DomainOption) => {
    try {
      await onDomainSelected?.(d.project_id);
    } catch {
      /* best-effort; the propose call still scopes via project_id */
    }
    await startSuggest(d.project_id);
  };

  const prepare = async () => {
    if (!proposal) return;
    setError(null);
    setPhase('accepting');
    try {
      const res = await recommendAccept({
        projectId: proposal.domain.project_id,
        channel: proposal.channel as BriefChannel,
        topic: proposal.topic,
      });
      // Blog/article intent FAILS HONESTLY (422 blog_draft_unavailable): render a
      // clean roadmap card with the PRESERVED topic + SEO evidence — never a
      // generic error, never a fabricated draft. `proposal` stays in state so the
      // card shows the preserved evidence + domain it already holds. This is NOT
      // a retryable error, so no Try-again is offered.
      // Blog/article intent SUCCEEDS (HTTP 200): a POPULATED long-form
      // blog_article draft. Render the long-form view. (Contract-first: the
      // orchestrator conforms the route so accept returns next.flow==='blog_draft'.)
      if (isRecommendBlogDraft(res)) {
        setBlogDraft(res.draft);
        setPhase('blog_draft');
        return;
      }
      // Blog/article FAILS or flag-off (HTTP 422): the EXISTING honest card,
      // behaviour UNCHANGED — never a generic error, never a fabricated draft.
      if (isRecommendUnavailable(res)) {
        setUnavailableNote(res.error.message);
        setPhase('blog_unavailable');
        return;
      }
      // Refuse to proceed unless connectors are provably fully gated.
      if (!isFullyGated(res.connectors)) {
        setPhase('error');
        setError('This action cannot be prepared — connector safety check failed. Nothing was changed.');
        return;
      }
      setAction(res.content_action);
      setQuestions(res.next.questions);
      setIntakeModule(res.next.intake_module);
      setConnectors(res.connectors);
      setStepIndex(0);
      setAnswers({});
      setInput('');
      // No missing questions -> straight to review.
      setPhase(briefSteps(res.next.questions).length === 0 ? 'review' : 'brief');
    } catch {
      failed();
    }
  };

  const saveCurrent = (value: string) => {
    const step = currentStep(questions, stepIndex);
    if (!step) return answers;
    const nextAnswers = { ...answers, [step.field]: value };
    setAnswers(nextAnswers);
    return nextAnswers;
  };

  const goNext = () => {
    saveCurrent(input.trim());
    if (isLastStep(questions, stepIndex)) {
      setPhase('review');
    } else {
      const ni = stepIndex + 1;
      setStepIndex(ni);
      const nextStep = currentStep(questions, ni);
      setInput(nextStep ? (answers[nextStep.field] ?? '') : '');
    }
  };

  const goSkip = () => {
    saveCurrent('');
    if (isLastStep(questions, stepIndex)) {
      setPhase('review');
    } else {
      const ni = stepIndex + 1;
      setStepIndex(ni);
      const nextStep = currentStep(questions, ni);
      setInput(nextStep ? (answers[nextStep.field] ?? '') : '');
    }
  };

  const goBack = () => {
    saveCurrent(input.trim());
    if (stepIndex === 0) {
      setPhase('proposal');
      return;
    }
    const pi = stepIndex - 1;
    setStepIndex(pi);
    const prevStep = currentStep(questions, pi);
    setInput(prevStep ? (answers[prevStep.field] ?? '') : '');
  };

  const revise = () => {
    setStepIndex(0);
    const first = currentStep(questions, 0);
    setInput(first ? (answers[first.field] ?? '') : '');
    setPhase('brief');
  };

  const createDraft = async () => {
    if (!action) return;
    setError(null);
    setPhase('submitting');
    const merged = mergeBriefAnswers(action, answers);
    try {
      const handoff = await submitContentActionBrief(merged, intakeModule, answers);
      setAction(merged);
      if (handoff.status === 'created' && handoff.url) {
        setDraftUrl(handoff.url);
        setPhase('draft_ready');
      } else if (handoff.status === 'needs_detail' && handoff.followUp) {
        setFollowUp(handoff.followUp);
        setPhase('needs_detail');
      } else {
        setPhase('handoff_pending');
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        setPhase('error');
        setError('That domain is no longer connected to this workspace. Nothing was changed.');
        return;
      }
      failed();
    }
  };

  const addDetails = () => {
    if (!followUp || followUp.questions.length === 0) return;
    setQuestions(followUp.questions);
    setStepIndex(0);
    const first = followUp.questions[0];
    setInput(first ? (answers[first.field] ?? '') : '');
    setPhase('brief');
  };

  const openDraft = () => {
    if (draftUrl) navigate(draftUrl);
  };

  // Copy the PRESERVED blog brief (topic + SEO evidence) built from the proposal
  // the panel already holds — the same evidence the server preserved server-side.
  const copyUnavailableBrief = async () => {
    if (!proposal) return;
    const preserved: ContentAction = {
      source: 'aura_action',
      workspace_scoped: true,
      brief: {
        channel: proposal.channel,
        objective: '',
        audience: '',
        topic: proposal.topic,
        tone: '',
        format: '',
        cta: '',
        timing_hint: '',
      },
      evidence: proposal.evidence,
      domain: proposal.domain,
      missing_brief_fields: [],
    };
    try {
      await navigator.clipboard.writeText(unavailableBriefText(preserved, unavailableNote));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  const copyBrief = async () => {
    if (!action) return;
    try {
      await navigator.clipboard.writeText(briefToPlainText(mergeBriefAnswers(action, answers)));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  // ---- render --------------------------------------------------------------

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={() => void startSuggest()}
        className="flex w-full items-center gap-2 rounded-md border border-dashed border-ai/40 bg-ai-wash/20 px-3 py-2 text-left text-xs text-text-secondary transition-colors duration-fast hover:border-ai hover:bg-ai-wash/40 max-lg:min-h-[44px]"
      >
        <span aria-hidden className="text-ai">✧</span>
        <span>
          <span className="font-semibold text-text-primary">Suggest a content action</span>
          <span className="block text-[11px] text-text-tertiary">
            Aura reads your SEO signals and proposes a draft to prepare.
          </span>
        </span>
      </button>
    );
  }

  return (
    <section
      aria-label="Aura content action"
      className="rounded-md border border-ai/40 bg-ai-wash/40 p-3"
    >
      {(phase === 'proposing' || phase === 'accepting' || phase === 'submitting') && (
        <Thinking
          label={
            phase === 'submitting' ? 'Preparing your draft' : 'Aura is thinking'
          }
        />
      )}

      {phase === 'domain_choice' && (
        <div>
          <AdvisoryHeader label="✧ which domain?" />
          <CleanText text={reply} />
          <div className="mt-3 flex flex-wrap gap-2">
            {domainChoices.map((d) => (
              <button
                key={d.project_id}
                type="button"
                onClick={() => void pickDomain(d)}
                title={d.hostname}
                className="rounded-full border border-ai/40 bg-canvas px-3 py-1 text-xs font-medium text-text-primary transition-colors duration-fast hover:border-ai hover:bg-ai-wash/60 max-lg:min-h-[44px]"
              >
                {d.display_name || d.hostname}
              </button>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={reset} className={ghostBtn}>
              Not now
            </button>
          </div>
        </div>
      )}

      {phase === 'no_signal' && (
        <div>
          <AdvisoryHeader label="✧ proposed by copilot" />
          <CleanText text={reply} />
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={reset} className={ghostBtn}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {phase === 'proposal' && proposal && (
        <div>
          <AdvisoryHeader label="✧ proposed by copilot" />
          <div className="mb-2 inline-flex items-center gap-1 rounded-sm border border-ai/25 bg-canvas px-1.5 py-0.5 text-[10px] text-text-secondary">
            <span className="text-text-tertiary">Viewing:</span>
            <span className="font-medium text-text-primary">{proposal.domain.hostname}</span>
          </div>
          <CleanText text={reply} />

          {evidenceLines(proposal.evidence).length > 0 && (
            <ul className="mt-2 space-y-1">
              {evidenceLines(proposal.evidence).map((e, i) => (
                <li key={i} className="text-[11px] leading-snug text-text-secondary">
                  <span className="font-semibold text-text-tertiary">{e.label} · </span>
                  {e.summary}
                </li>
              ))}
            </ul>
          )}

          <p className="mt-2 text-[11px] text-text-tertiary">
            Suggested as a <span className="font-medium text-text-secondary">{channelLabel(proposal.channel)}</span>.
            You can refine everything next.
          </p>

          <ConnectorGatedNotice connectors={connectors} className="mt-3" />

          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => void prepare()} className={primaryBtn}>
              Prepare it
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Not now
            </button>
          </div>
        </div>
      )}

      {phase === 'brief' && (() => {
        const step = currentStep(questions, stepIndex);
        if (!step) return null;
        return (
          <div>
            <AdvisoryHeader label="✧ preparing a draft" />
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
              {stepProgressLabel(questions, stepIndex)}
            </div>
            <label className="block text-sm font-medium text-text-primary" htmlFor="brief-answer">
              {step.question}
            </label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                goNext();
              }}
              className="mt-2"
            >
              <input
                id="brief-answer"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoComplete="off"
                placeholder="Type your answer…"
                className="w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm outline-none placeholder:text-text-tertiary focus:border-ai"
              />
              <div className="mt-3 flex items-center justify-between">
                <button type="button" onClick={goBack} className={outlineBtn}>
                  ← Back
                </button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={goSkip} className={ghostBtn}>
                    Skip
                  </button>
                  <button type="submit" className={primaryBtn}>
                    {isLastStep(questions, stepIndex) ? 'Review' : 'Next'}
                  </button>
                </div>
              </div>
            </form>
            <ConnectorGatedNotice connectors={connectors} className="mt-3" />
          </div>
        );
      })()}

      {phase === 'review' && action && (
        <div>
          <AdvisoryHeader label="✧ review your brief" />
          <p className="text-[11px] text-text-tertiary">
            Aura will open a DRAFT for your review — nothing is published or sent.
          </p>
          <dl className="mt-2 space-y-1.5">
            {briefReviewRows(mergeBriefAnswers(action, answers).brief).map((row) => (
              <div key={row.field} className="grid grid-cols-[88px_1fr] gap-2 text-xs">
                <dt className="text-text-tertiary">{row.label}</dt>
                <dd className="text-text-primary">
                  {row.value !== '' ? row.value : <span className="text-text-tertiary">—</span>}
                </dd>
              </div>
            ))}
          </dl>

          <ConnectorGatedNotice connectors={connectors} className="mt-3" />

          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => void createDraft()} className={primaryBtn}>
              Create draft
            </button>
            <button type="button" onClick={revise} className={outlineBtn}>
              Revise answers
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {phase === 'draft_ready' && (
        <div>
          <AdvisoryHeader label="✧ draft created" />
          <p className="text-sm text-text-primary">
            Your draft is ready and waiting for your review. Nothing has been published or sent.
          </p>
          <ConnectorGatedNotice connectors={connectors} className="mt-3" />
          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={openDraft} className={primaryBtn}>
              Open draft to review
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Done
            </button>
          </div>
        </div>
      )}

      {phase === 'handoff_pending' && (
        <div>
          <AdvisoryHeader label="✧ brief prepared" />
          <p className="text-sm text-text-primary">
            Your brief is ready. The draft workspace is being connected on this environment, so no
            draft was created yet — and nothing was published or sent. You can copy the brief to use
            it now.
          </p>
          <ConnectorGatedNotice connectors={connectors} className="mt-3" />
          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => void copyBrief()} className={outlineBtn}>
              {copied ? 'Copied ✓' : 'Copy brief'}
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Done
            </button>
          </div>
        </div>
      )}

      {phase === 'needs_detail' && followUp && (
        <div>
          <AdvisoryHeader label="✧ a little more detail" />
          <p className="text-sm text-text-primary">{followUp.note}</p>
          {followUp.questions.length > 0 && (
            <ul className="mt-2 space-y-1">
              {followUp.questions.map((q) => (
                <li key={q.field} className="text-[11px] leading-snug text-text-secondary">
                  <span aria-hidden>• </span>
                  {q.question}
                </li>
              ))}
            </ul>
          )}
          {followUp.segments.length > 0 && (
            <p className="mt-2 text-[11px] text-text-secondary">
              <span className="font-semibold text-text-tertiary">Available audiences: </span>
              {followUp.segments.map((seg) => seg.name).filter(Boolean).join(', ')}
            </p>
          )}
          <ConnectorGatedNotice connectors={connectors} className="mt-3" />
          <div className="mt-3 flex items-center gap-2">
            {followUp.questions.length > 0 && (
              <button type="button" onClick={addDetails} className={primaryBtn}>
                Add details
              </button>
            )}
            <button type="button" onClick={() => void copyBrief()} className={outlineBtn}>
              {copied ? 'Copied ✓' : 'Copy brief'}
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {phase === 'blog_unavailable' && proposal && (
        <div>
          <AdvisoryHeader label="✧ on the roadmap" />
          <h3 className="text-sm font-semibold text-text-primary">
            Blog Draft isn’t available yet
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-text-secondary">
            {unavailableNote ||
              'It’s on the roadmap. I’ve kept your SEO recommendation and the evidence so we can prepare it properly soon. Nothing was created, published or sent.'}
          </p>

          <div className="mt-3 rounded-md border border-border-default bg-canvas p-2.5">
            <div className="mb-1 inline-flex items-center gap-1 text-[10px] text-text-secondary">
              <span className="text-text-tertiary">Viewing:</span>
              <span className="font-medium text-text-primary">{proposal.domain.hostname}</span>
            </div>
            <p className="text-xs text-text-primary">
              <span className="text-text-tertiary">Topic · </span>
              {proposal.topic}
            </p>
            {evidenceLines(proposal.evidence).length > 0 && (
              <ul className="mt-2 space-y-1">
                {evidenceLines(proposal.evidence).map((e, i) => (
                  <li key={i} className="text-[11px] leading-snug text-text-secondary">
                    <span className="font-semibold text-text-tertiary">{e.label} · </span>
                    {e.summary}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => void copyUnavailableBrief()} className={outlineBtn}>
              {copied ? 'Copied ✓' : 'Copy brief'}
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {phase === 'blog_draft' && blogDraft && (
        <BlogDraftView
          draft={blogDraft}
          onDismiss={reset}
          projectId={proposal?.domain?.project_id ?? activeProjectId ?? null}
          onRepurpose={() => {
            // New social DRAFTS were created — refresh any Content Studio content
            // list so they surface for review. Draft-only; nothing is sent.
            void queryClient.invalidateQueries({ queryKey: ['social-content'] });
          }}
          onSaved={setBlogDraft}
        />
      )}

      {phase === 'error' && (
        <div>
          <AdvisoryHeader label="✧ nothing changed" />
          <p className="text-sm text-text-secondary">{error}</p>
          <div className="mt-3 flex items-center gap-2">
            <button type="button" onClick={() => void startSuggest()} className={outlineBtn}>
              Try again
            </button>
            <button type="button" onClick={reset} className={ghostBtn}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* paced spoken segments are available for the voice surface; shown here as
          discrete lines so the narration is clean and segmented, never raw. */}
      {segments.length > 1 && (phase === 'proposal' || phase === 'no_signal') && (
        <div className="sr-only" aria-hidden data-testid="narration-segments">
          {segments.map((s, i) => (
            <span key={i}>{s.text}</span>
          ))}
        </div>
      )}
    </section>
  );
}
